import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Banknote, Coins, TrendingUp, RefreshCw } from 'lucide-react';
import CashboxPanel from '../components/CashboxPanel';
import IngresosPanel from '../components/IngresosPanel';
import AvisoConteoVitrina from '../components/ConteoVitrina';
import { syncKydemy } from '../api';
import { permisosVitrina, RespuestaPermisos } from '../vitrinaApi';
import { api } from '../../../services/apiClient';
import { useAuth } from '../../../context/AuthContext';
import { useToast } from '../../../context/ToastContext';

/**
 * Caja como pantalla PRINCIPAL (sidebar), igual que en la agenda dental: la operación diaria
 * (abrir, cobrar, arquear) no vive enterrada en Finanzas.
 *
 * Pestañas (audio de Carlos 18-sep-2026, sesión con Karla): en Kydemy, al entrar a la caja salen
 * cuatro — Ingresos · Gastos · Deudas · Efectivo. Van saliendo por fases: F1 «Efectivo» (lo que
 * antes era toda esta pantalla) y F2 «Ingresos» (la que Kydemy llama Payments). Gastos y Deudas
 * quedan para después; ella misma los puso al final.
 *
 * La pestaña viaja en la URL (`/caja?t=ingresos`) para poder enlazar y para que recargar no
 * devuelva siempre a Efectivo.
 */
const TABS = [
  { k: 'efectivo', label: 'Efectivo', icon: Coins },
  { k: 'ingresos', label: 'Ingresos', icon: TrendingUp },
] as const;
type TabKey = (typeof TABS)[number]['k'];

export default function CajaPage() {
  const { user } = useAuth();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [sincro, setSincro] = useState(false);
  // Se incrementa al terminar una sincronización: los paneles lo reciben y recargan.
  const [recarga, setRecarga] = useState(0);
  const inicial = TABS.some((t) => t.k === params.get('t')) ? (params.get('t') as TabKey) : 'efectivo';
  const [tab, setTab] = useState<TabKey>(inicial);
  const [abrirPago] = useState(() => params.get('nuevo') === 'pago');
  // ¿Puede contar la vitrina? Arranca en «no» para no enseñar un botón que luego rebote.
  const [puedeContar, setPuedeContar] = useState(false);
  useEffect(() => {
    api.get<RespuestaPermisos>('/me/permissions').then((r) => setPuedeContar(permisosVitrina(r).contar)).catch(() => setPuedeContar(true));
  }, []);
  useEffect(() => {
    if (params.get('nuevo') !== 'pago') return;
    const p = new URLSearchParams(params); p.delete('nuevo'); setParams(p, { replace: true });
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  // La pestaña se leía SOLO al montar: un enlace a `/caja?t=ingresos` desde dentro de la propia
  // página (react-router no re-monta) cambiaba la URL y dejaba la pestaña donde estaba. Con esto
  // también funcionan las flechas atrás/adelante del navegador.
  useEffect(() => {
    const q = params.get('t');
    const siguiente = (TABS.some((t) => t.k === q) ? q : 'efectivo') as TabKey;
    setTab((actual) => (actual === siguiente ? actual : siguiente));
  }, [params]);

  function elegir(k: TabKey) {
    setTab(k);
    const p = new URLSearchParams(params);
    if (k === 'efectivo') p.delete('t'); else p.set('t', k);
    setParams(p, { replace: true });
  }

  /** Trae de Kydemy las cajas y los cobros del día (~4 s) y recarga lo que se esté viendo.
   *  Hace falta mientras Karla siga cobrando allá: lo suyo entra por el ETL, no en directo. */
  async function actualizar() {
    setSincro(true);
    try {
      const r = await syncKydemy();
      const n = r.pagos_nuevos + r.cajas_nuevas;
      toast[n > 0 ? 'success' : 'info'](
        n === 0 ? `Ya estaba al día (${r.segundos}s)`
          : `${r.pagos_nuevos ? `${r.pagos_nuevos} cobro(s)` : ''}${r.pagos_nuevos && r.cajas_nuevas ? ' y ' : ''}${r.cajas_nuevas ? `${r.cajas_nuevas} caja(s)` : ''} desde Kydemy`);
      setRecarga((k) => k + 1);
    } catch (e: any) {
      toast.error(e?.message || 'No se pudo actualizar desde Kydemy');
    } finally { setSincro(false); }
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 font-display text-2xl font-bold text-content">
          <Banknote className="text-brand-blue" size={24} /> Caja
        </h2>
        {user?.role === 'admin' && (
          <button onClick={actualizar} disabled={sincro}
            title="Trae de Kydemy los cobros y las cajas de hoy"
            className="no-print flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-1.5 text-sm font-medium text-content-2 transition hover:bg-surface-2 disabled:opacity-50">
            <RefreshCw size={15} className={sincro ? 'animate-spin' : ''} />
            {sincro ? 'Actualizando…' : 'Actualizar'}
          </button>
        )}
      </div>

      {/* Falta el conteo de vitrina del turno (solo con el inventario encendido). Sin `puedeContar` el botón
          salía a cualquiera y el servidor lo rechazaba (Aracely, 1-oct-2026, 3 veces). */}
      <AvisoConteoVitrina puedeContar={puedeContar} />

      <div className="no-print mb-4 inline-flex rounded-lg border border-line bg-surface p-0.5">
        {TABS.map(({ k, label, icon: Icon }) => (
          <button key={k} onClick={() => elegir(k)}
            className={`flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-medium transition ${
              tab === k ? 'bg-accent text-on-accent shadow-sm' : 'text-content-2 hover:text-content'}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {/* ?nuevo=pago = atajo del botón «+» del móvil: abre «Registrar pago» (antes en Finanzas). */}
      {tab === 'ingresos' ? <IngresosPanel recarga={recarga} abrirFormInicial={abrirPago} /> : <CashboxPanel recarga={recarga} />}
    </div>
  );
}
